import React, {useCallback, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {message} from 'antd';

import apiClient from 'utils/apiClient';
import {config} from 'config/config';
import {useLocaleContext} from 'context/LocaleContext';
import {VideoInstructionInterface} from 'components/VideoInstructions/types';

// Note: this file needs a .tsx extension (not .ts) because loadPositions
// builds its option labels as <FormattedMessage/> elements, same as the
// original VideoInstructions component did.
export type PositionOptions = Record<number, React.ReactNode>;

interface UseVideoInstructionsResult {
    instructions: VideoInstructionInterface[];
    instructionsLoading: boolean;
    positions?: PositionOptions;
    positionsLoading: boolean;
    reload: (position?: number) => Promise<void>;
}

// Positions and the instruction list both require acl:view_instruction; the
// caller passes enabled=false for roles without it so we never fire a
// request that can only come back 403.
export const useVideoInstructions = (enabled: boolean): UseVideoInstructionsResult => {
    const intl = useIntl();
    const {locale} = useLocaleContext();

    const [instructions, setInstructions] = useState<VideoInstructionInterface[]>([]);
    const [instructionsLoading, setInstructionsLoading] = useState(false);
    const [positions, setPositions] = useState<PositionOptions>();
    const [positionsLoading, setPositionsLoading] = useState(false);

    const loadInstructions = useCallback(async (position: number = 0) => {
        setInstructionsLoading(true);

        const parsedValues: {language: string, position?: number} = {language: locale};
        if (position !== 0) {
            parsedValues.position = position;
        }

        try {
            const response = await apiClient.request('/api/v1/instruction/list', parsedValues, 'POST');
            setInstructions(response.instructions);
        } catch (error) {
            console.error(error);
            message.error(intl.formatMessage({id: 'error.data_load'}));
        } finally {
            setInstructionsLoading(false);
        }
    }, [locale, intl]);

    const loadPositions = useCallback(async () => {
        setPositionsLoading(true);

        try {
            const response = await apiClient.request(config.api.routes.backend.instructions.positions, {}, 'GET');
            const positionOptions: PositionOptions = {};

            Object.entries(response.instructionPositions).forEach(([key, value]: [string, any]) => {
                positionOptions[Number(key)] = <FormattedMessage id={'system.positions_' + value}/>;
            });

            setPositions(positionOptions);
        } catch (error) {
            console.error(error);
            message.error(intl.formatMessage({id: 'error.data_load'}));
        } finally {
            setPositionsLoading(false);
        }
    }, [intl]);

    useEffect(() => {
        if (!enabled) return;
        loadPositions();
        loadInstructions();
    }, [enabled, locale, loadPositions, loadInstructions]);

    return {instructions, instructionsLoading, positions, positionsLoading, reload: loadInstructions};
};
